import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  Chip,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useTranslation } from "react-i18next";
import { format, parseISO, differenceInDays } from "date-fns";
import leaveApiService from "../services/leave-api";
import holidayApiService from "../services/holiday-api";
import {
  LeaveBalance,
  LeaveType,
  LeaveRequest,
  CreateLeaveRequest,
} from "../types/leave";

interface EditRequestDialogProps {
  open: boolean;
  request: LeaveRequest | null;
  onClose: () => void;
  onSuccess: () => void;
}

const EditRequestDialog: React.FC<EditRequestDialogProps> = ({
  open,
  request,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form data
  const [selectedType, setSelectedType] = useState<LeaveType | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [reason, setReason] = useState("");
  const [isHalfDay, setIsHalfDay] = useState(false);
  const [halfDayPeriod, setHalfDayPeriod] = useState<
    "MORNING" | "AFTERNOON" | ""
  >("");

  // Data
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
  const [balances, setBalances] = useState<LeaveBalance[]>([]);
  const [nonWorkdayDates, setNonWorkdayDates] = useState<Set<string>>(
    new Set(),
  );
  const [userOfficeLocation, setUserOfficeLocation] = useState<string>("");

  // Latest date an employee may file/edit for: Dec 31 of the next calendar year.
  const maxFilingDate = useMemo(
    () => new Date(new Date().getFullYear() + 1, 11, 31),
    [],
  );

  // Helper function to format date as local date string (YYYY-MM-DD)
  const formatLocalDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Calculated values
  const daysRequested = useMemo(() => {
    if (!startDate || !endDate) return 0;
    if (isHalfDay) return 0.5;

    // Calculate workdays by excluding non-workdays (weekends + holidays - workday overrides)
    const totalCalendarDays = differenceInDays(endDate, startDate) + 1;
    let workdayCount = 0;

    // Iterate through each day and count only workdays
    for (let i = 0; i < totalCalendarDays; i++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(currentDate.getDate() + i);
      const dateStr = formatLocalDate(currentDate);

      // If date is NOT in nonWorkdayDates, it's a workday
      if (!nonWorkdayDates.has(dateStr)) {
        workdayCount++;
      }
    }

    return workdayCount;
  }, [startDate, endDate, isHalfDay, nonWorkdayDates]);

  const selectedBalance = useMemo(
    () =>
      balances.find((b) => selectedType && b.leaveTypeId === selectedType.id),
    [balances, selectedType],
  );

  // Memoize filtered leave types to prevent expensive re-renders
  const filteredLeaveTypes = useMemo(
    () =>
      leaveTypes
        .filter((type) => {
          const balance = balances.find((b) => b.leaveTypeId === type.id);
          // Always show Unpaid Leave even if 0 days, filter out others with 0 days
          return (
            !balance || balance.availableDays > 0 || type.code === "UNPAID"
          );
        })
        .map((type) => {
          const balance = balances.find((b) => b.leaveTypeId === type.id);
          return { type, balance };
        }),
    [leaveTypes, balances],
  );

  useEffect(() => {
    if (open && request) {
      loadData();
      // Initialize form with request data
      try {
        let parsedStartDate: Date;
        let parsedEndDate: Date;

        // Handle different date formats from backend
        if (Array.isArray(request.startDate)) {
          // Backend sends date as array [year, month, day]
          const [year, month, day] = request.startDate as number[];
          parsedStartDate = new Date(year, month - 1, day); // month is 0-based in JS
        } else if (typeof request.startDate === "string") {
          // Backend sends date as ISO string
          parsedStartDate = parseISO(request.startDate);
        } else {
          throw new Error(t("error.invalidDateFormat"));
        }

        if (Array.isArray(request.endDate)) {
          // Backend sends date as array [year, month, day]
          const [year, month, day] = request.endDate as number[];
          parsedEndDate = new Date(year, month - 1, day); // month is 0-based in JS
        } else if (typeof request.endDate === "string") {
          // Backend sends date as ISO string
          parsedEndDate = parseISO(request.endDate);
        } else {
          throw new Error(t("error.invalidDateFormat"));
        }

        // Check if dates are valid
        if (
          isNaN(parsedStartDate.getTime()) ||
          isNaN(parsedEndDate.getTime())
        ) {
          console.error(
            t("error.invalidDatesInRequest"),
            request.startDate,
            request.endDate,
          );
          setError(t("error.invalidDatesInRequest"));
          return;
        }

        setStartDate(parsedStartDate);
        setEndDate(parsedEndDate);
      } catch (err) {
        console.error(
          t("error.errorParsingDates"),
          err,
          "Start:",
          request.startDate,
          "End:",
          request.endDate,
        );
        setError(t("error.errorParsingDates"));
        return;
      }

      setReason(request.reason || "");
      setIsHalfDay(request.isHalfDay || false);
      setHalfDayPeriod(request.halfDayPeriod || "");
    }
  }, [open, request]);

  useEffect(() => {
    if (request && leaveTypes.length > 0) {
      const type = leaveTypes.find((t) => t.id === request.leaveTypeId);
      setSelectedType(type || null);
    }
  }, [request, leaveTypes]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [typesResponse, balancesResponse, profileResponse] =
        await Promise.all([
          leaveApiService.getActiveLeaveTypes(),
          leaveApiService.getMyBalances(),
          leaveApiService.getMyProfile(),
        ]);

      setLeaveTypes(typesResponse);
      setBalances(balancesResponse);

      // Get user's office location
      const officeLocation = profileResponse.officeLocation || "";
      setUserOfficeLocation(officeLocation);

      // Load non-workdays across a wide window (previous calendar year through
      // Dec 31 of the next calendar year) so both backdated and next-year
      // requests being edited have accurate weekend/holiday data.
      const today = new Date();
      const windowStart = new Date(today.getFullYear() - 1, 0, 1);
      const windowEnd = new Date(today.getFullYear() + 1, 11, 31);

      // Format dates as local dates (YYYY-MM-DD) without timezone conversion
      const startDateStr = formatLocalDate(windowStart);
      const endDateStr = formatLocalDate(windowEnd);

      try {
        const nonWorkdays = await holidayApiService.getNonWorkdaysByDateRange(
          startDateStr,
          endDateStr,
          officeLocation,
        );
        setNonWorkdayDates(new Set(nonWorkdays));
      } catch (err) {
        console.error(t("error.errorLoadingNonWorkdays"), err);
        // Continue without non-workday filtering if it fails
      }
    } catch (err) {
      console.error(t("error.errorLoadingData"), err);
      setError(t("leave.messages.loadError"));
    } finally {
      setLoading(false);
    }
  };

  const shouldDisableDate = (date: Date): boolean => {
    // Format date as local date string (YYYY-MM-DD) without timezone conversion
    const dateStr = formatLocalDate(date);
    // Disable if it's a non-workday (holiday, weekend, or workday override)
    return nonWorkdayDates.has(dateStr);
  };

  const shouldDisableStartDate = (date: Date): boolean => {
    // Backdated filing is allowed: no minimum-date floor. Only non-workdays
    // (holidays/weekends that are not overridden) are disabled.
    return shouldDisableDate(date);
  };

  const shouldDisableEndDate = (date: Date): boolean => {
    // Disable non-workdays
    if (shouldDisableDate(date)) {
      return true;
    }
    // If start date is selected, disable dates before start date
    if (startDate && date < startDate) {
      return true;
    }
    return false;
  };

  const handleSubmit = async () => {
    if (!request || !selectedType || !startDate || !endDate) {
      setError(t("leave.messages.fillRequired"));
      return;
    }

    if (selectedBalance && daysRequested > selectedBalance.availableDays) {
      setError(t("leave.messages.insufficientBalance"));
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      if (!startDate || !endDate) {
        setError(t("error.fillAllRequiredFields"));
        return;
      }

      const requestData: CreateLeaveRequest = {
        leaveTypeId: selectedType.id,
        startDate: format(startDate, "yyyy-MM-dd"),
        endDate: format(endDate, "yyyy-MM-dd"),
        reason: reason.trim() || undefined,
        isHalfDay: isHalfDay,
        halfDayPeriod:
          isHalfDay && halfDayPeriod
            ? (halfDayPeriod as "MORNING" | "AFTERNOON")
            : undefined,
      };

      await leaveApiService.updateLeaveRequest(request.id, requestData);
      onSuccess();
      handleClose();
    } catch (err: any) {
      console.error(t("error.failedToUpdateRequest"), err);
      setError(err.response?.data?.message || t("leave.messages.updateError"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedType(null);
    setStartDate(null);
    setEndDate(null);
    setReason("");
    setError(null);
    onClose();
  };

  const isFormValid =
    selectedType && startDate && endDate && startDate <= endDate;
  const canEdit = request && request.status === "PENDING";

  if (!canEdit) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{t("leave.editRequest")}</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            {t("leave.messages.cannotEditApproved")}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t("common.close")}</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{t("leave.editRequest")}</DialogTitle>

      <DialogContent>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ pt: 1 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Grid container spacing={3}>
              {/* Leave Type Selection */}
              <Grid size={{ xs: 12 }}>
                <TextField
                  select
                  fullWidth
                  label={t("leave.leaveType")}
                  value={selectedType?.id || ""}
                  onChange={(e) => {
                    const type = leaveTypes.find(
                      (t) => t.id === Number(e.target.value),
                    );
                    setSelectedType(type || null);
                  }}
                  required
                >
                  {filteredLeaveTypes.map(({ type, balance }) => (
                    <MenuItem key={type.id} value={type.id}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                      >
                        <span>{type.name}</span>
                        {balance && (
                          <Chip
                            label={`${balance.availableDays} ${t("leave.days")}`}
                            size="small"
                            color={
                              balance.availableDays > 0 ? "primary" : "default"
                            }
                          />
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Date Selection */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label={t("leave.startDate")}
                    value={startDate}
                    maxDate={maxFilingDate}
                    onChange={(newDate) => {
                      setStartDate(newDate);
                      // Auto-populate end date if it's empty
                      if (newDate && !endDate) {
                        setEndDate(newDate);
                      }
                    }}
                    shouldDisableDate={shouldDisableStartDate}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label={t("leave.endDate")}
                    value={endDate}
                    maxDate={maxFilingDate}
                    onChange={setEndDate}
                    shouldDisableDate={shouldDisableEndDate}
                    defaultCalendarMonth={startDate || undefined}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              {/* Half-day options */}
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isHalfDay}
                      onChange={(e) => {
                        const halfDay = e.target.checked;
                        setIsHalfDay(halfDay);
                        if (halfDay && startDate) {
                          setEndDate(startDate);
                        } else {
                          setHalfDayPeriod("");
                        }
                      }}
                    />
                  }
                  label={t("leave.halfDay")}
                />
              </Grid>

              {isHalfDay && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>{t("leave.halfDayPeriod")}</InputLabel>
                    <Select
                      value={halfDayPeriod}
                      onChange={(e) =>
                        setHalfDayPeriod(
                          e.target.value as "MORNING" | "AFTERNOON",
                        )
                      }
                      label={t("leave.halfDayPeriod")}
                      required
                    >
                      <MenuItem value="MORNING">{t("leave.morning")}</MenuItem>
                      <MenuItem value="AFTERNOON">
                        {t("leave.afternoon")}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {/* Request Summary */}
              {selectedType && startDate && endDate && (
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.05)"
                          : "grey.50",
                      borderRadius: 1,
                      border: (theme) => `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Typography variant="subtitle2" gutterBottom>
                      {t("leave.requestSummary")}
                    </Typography>
                    <Typography variant="body2">
                      <strong>{selectedType.name}</strong>
                      {" : "}
                      {startDate && endDate
                        ? `${format(startDate, "MMM dd, yyyy")} - ${format(endDate, "MMM dd, yyyy")}`
                        : t("common.date")}
                      <br />
                      <strong>
                        {daysRequested}{" "}
                        {daysRequested === 1 ? t("leave.day") : t("leave.days")}
                      </strong>
                      {isHalfDay && halfDayPeriod && (
                        <Chip
                          label={`${t("leave.halfDay")} - ${halfDayPeriod === "MORNING" ? "AM" : "PM"}`}
                          size="small"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Typography>
                    {selectedBalance && (
                      <>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 1 }}
                        >
                          {t("leave.availableBalance")}:{" "}
                          {selectedBalance.availableDays}{" "}
                          {selectedBalance.availableDays === 1
                            ? t("leave.day")
                            : t("leave.days")}
                        </Typography>
                        <Typography
                          variant="body2"
                          color={
                            selectedBalance.availableDays - daysRequested < 0
                              ? "error"
                              : "success.main"
                          }
                          sx={{ mt: 0.5, fontWeight: 600 }}
                        >
                          {t("leave.balanceAfter")}:{" "}
                          {(
                            selectedBalance.availableDays - daysRequested
                          ).toFixed(1)}{" "}
                          {Math.abs(
                            selectedBalance.availableDays - daysRequested,
                          ) === 1
                            ? t("leave.day")
                            : t("leave.days")}
                        </Typography>
                      </>
                    )}
                  </Box>
                </Grid>
              )}

              {/* Reason */}
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label={t("leave.reason")}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={t("leave.reasonPlaceholder")}
                />
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          {t("common.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isFormValid || submitting || loading}
          startIcon={submitting ? <CircularProgress size={20} /> : null}
        >
          {submitting ? t("leave.updating") : t("leave.updateRequest")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditRequestDialog;
